import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useCurrency } from '../../context/CurrencyContext';
import {
  adminFetchAllPlans, adminCreatePlan, adminUpdatePlan,
  adminTogglePlan, adminDeletePlan, formatStorage, adminTriggerSeed
} from '../../services/plansService';

const {
  FiPackage, FiPlus, FiEdit2, FiTrash2, FiToggleLeft, FiToggleRight,
  FiSave, FiX, FiCheck, FiAlertTriangle, FiLoader, FiRefreshCw,
  FiDollarSign, FiHardDrive, FiTag, FiList, FiStar, FiEye, FiEyeOff,
  FiGrid, FiSliders, FiInfo, FiChevronDown, FiChevronUp, FiLock,
  FiZap, FiArrowUp, FiArrowDown, FiCopy, FiLayers,
  FiTrendingUp, FiShield, FiAward, FiGlobe, FiActivity,
  FiFileText, FiFilter,
} = FiIcons;

const BILLING_LABELS = { month: '/mo', year: '/yr', 'one-time': '', forever: '' };
const BILLING_OPTS = [
  { value: 'month',    label: 'Monthly',      badge: '/mo'      },
  { value: 'year',     label: 'Annually',     badge: '/yr'      },
  { value: 'one-time', label: 'One-Time',     badge: 'once'     },
  { value: 'forever',  label: 'Free Forever', badge: 'Free'     },
];
const COLOR_PRESETS = [
  { label: 'Slate',   value: 'from-gray-400 to-gray-600'       },
  { label: 'Blue',    value: 'from-blue-500 to-blue-700'       },
  { label: 'Gold',    value: 'from-amber-400 to-amber-600'     },
  { label: 'Purple',  value: 'from-purple-500 to-purple-700'   },
  { label: 'Emerald', value: 'from-emerald-500 to-emerald-700' },
  { label: 'Rose',    value: 'from-rose-500 to-rose-700'       },
  { label: 'Navy',    value: 'from-accent-navy to-[#1a2a5e]'   },
];
const ADMIN_TOKEN = import.meta.env.VITE_ADMIN_TOKEN || 'dev-admin-secret-2025';

// ── Plan type detection ───────────────────────────────────────────────────────
const isWillPlan  = (p) => p?.plan_key?.startsWith('will_');
const isDocuPlan  = (p) => !isWillPlan(p);

// ── Plan-type tab config ──────────────────────────────────────────────────────
const PLAN_TYPE_TABS = [
  {
    id: 'all',
    label: 'All Plans',
    icon: FiPackage,
    desc: 'DocuVault storage tiers + Will & Legacy plans',
    filter: () => true,
  },
  {
    id: 'docuvault',
    label: 'DocuVault Tiers',
    icon: FiHardDrive,
    desc: 'Storage subscription tiers (Free, Starter, Family, Legacy)',
    filter: isDocuPlan,
    badge: { text: 'Storage', color: 'bg-indigo-100 text-indigo-700 border-indigo-200' },
  },
  {
    id: 'will',
    label: 'Will & Legacy Plans',
    icon: FiFileText,
    desc: 'Legal will drafting, Legacy Guardian, Estate Concierge',
    filter: isWillPlan,
    badge: { text: 'Legal', color: 'bg-amber-100 text-amber-700 border-amber-200' },
  },
];

const STARTER_TEMPLATES = [
  {
    label: 'Free Tier', icon: FiShield, color: 'from-gray-400 to-gray-600',
    bg: 'bg-gray-50', border: 'border-gray-200', iconColor: 'text-gray-500',
    description: 'A no-cost entry plan to attract new users',
    preset: { plan_key: 'free', name: 'Free', description: 'Get started at no cost', storage_mb: 100, price_usd: 0, billing_cycle: 'forever', features: 'Up to 100 MB storage\n1 memorial page\nBasic guestbook\nCommunity support', highlight: false, is_active: true, sort_order: 0, color: 'from-gray-400 to-gray-600', badge: 'bg-gray-100 text-gray-600 border-gray-200' },
  },
  {
    label: 'Starter Plan', icon: FiTrendingUp, color: 'from-blue-500 to-blue-700',
    bg: 'bg-blue-50', border: 'border-blue-200', iconColor: 'text-blue-600',
    description: 'An affordable paid tier for individuals',
    preset: { plan_key: 'starter', name: 'Starter', description: 'Perfect for individuals', storage_mb: 512, price_usd: 4.99, billing_cycle: 'month', features: '512 MB secure storage\n3 memorial pages\nPhoto gallery\nEmail support', highlight: false, is_active: true, sort_order: 1, color: 'from-blue-500 to-blue-700', badge: 'bg-blue-50 text-blue-700 border-blue-200' },
  },
  {
    label: 'Family Plan', icon: FiAward, color: 'from-amber-400 to-amber-600',
    bg: 'bg-amber-50', border: 'border-amber-200', iconColor: 'text-amber-600',
    description: 'A popular mid-tier for families',
    preset: { plan_key: 'family', name: 'Family', description: 'Ideal for families', storage_mb: 5120, price_usd: 9.99, billing_cycle: 'month', features: '5 GB secure storage\nUnlimited memorial pages\nFamily tree builder\nAudio & video tributes\nPriority support', highlight: true, is_active: true, sort_order: 2, color: 'from-amber-400 to-amber-600', badge: 'bg-amber-50 text-amber-700 border-amber-200' },
  },
  {
    label: 'Legacy Plan', icon: FiLayers, color: 'from-purple-500 to-purple-700',
    bg: 'bg-purple-50', border: 'border-purple-200', iconColor: 'text-purple-600',
    description: 'A premium all-inclusive tier',
    preset: { plan_key: 'legacy', name: 'Legacy', description: 'The complete memorial experience', storage_mb: 20480, price_usd: 19.99, billing_cycle: 'month', features: '20 GB secure storage\nUnlimited everything\nAI obituary writer\nEstate management tools\nDocuVault access\nWhite-glove support', highlight: false, is_active: true, sort_order: 3, color: 'from-purple-500 to-purple-700', badge: 'bg-purple-50 text-purple-700 border-purple-200' },
  },
  {
    label: 'Will Draft', icon: FiFileText, color: 'from-blue-400 to-blue-600',
    bg: 'bg-blue-50', border: 'border-blue-200', iconColor: 'text-blue-600',
    description: 'One-time AI will generation',
    preset: { plan_key: 'will_draft', name: 'Will Draft', description: 'One-time generation', storage_mb: 0, price_usd: 29, billing_cycle: 'one-time', features: 'Full AI-guided 4-step interview\nJurisdiction-aware legal language (30+ countries)\nDownloadable PDF will draft\nExecutor & beneficiary clauses\nLegal disclaimer & next-steps guide', highlight: false, is_active: true, sort_order: 10, color: 'from-blue-400 to-blue-600', badge: '' },
  },
  {
    label: 'Legacy Guardian', icon: FiShield, color: 'from-accent-navy to-[#1a2a5e]',
    bg: 'bg-indigo-50', border: 'border-indigo-200', iconColor: 'text-indigo-600',
    description: 'Monthly will management subscription',
    preset: { plan_key: 'will_guardian', name: 'Legacy Guardian', description: 'Ongoing will management', storage_mb: 50, price_usd: 15, billing_cycle: 'month', features: 'Everything in Will Draft\nUnlimited will updates & revisions\nAnnual legal check-in reminders\nLife-event alerts\nDigital asset vault (up to 50 entries)\n1x attorney consultation per year\nBeneficiary notification service\nPriority email & chat support', highlight: true, is_active: true, sort_order: 11, color: 'from-accent-navy to-[#1a2a5e]', badge: 'Most Popular' },
  },
  {
    label: 'Estate Concierge', icon: FiStar, color: 'from-purple-600 to-indigo-900',
    bg: 'bg-purple-50', border: 'border-purple-200', iconColor: 'text-purple-600',
    description: 'Full estate management with human concierge',
    preset: { plan_key: 'will_estate', name: 'Estate Concierge', description: 'Complete estate management', storage_mb: 1024, price_usd: 39, billing_cycle: 'month', features: 'Everything in Legacy Guardian\nUnlimited digital asset vault entries\nMulti-jurisdiction document management\nDedicated estate concierge (human)\nQuarterly attorney reviews\nFamily member sub-accounts (up to 4)\nTrust & power of attorney drafting\nProbate assistance & filing guidance\nWhite-glove document notarisation service', highlight: false, is_active: true, sort_order: 12, color: 'from-purple-600 to-indigo-900', badge: 'Full Protection' },
  },
];

const emptyForm = () => ({
  plan_key: '', name: '', description: '',
  storage_mb: 0, price_usd: 9.99, billing_cycle: 'month',
  features: '', highlight: false, is_active: true,
  sort_order: 0, color: 'from-blue-500 to-blue-700',
  badge: '',
});
const planToForm = (plan) => ({
  plan_key:      plan.plan_key,
  name:          plan.name,
  description:   plan.description || '',
  storage_mb:    plan.storage_mb,
  price_usd:     plan.price_usd,
  billing_cycle: plan.billing_cycle,
  features:      Array.isArray(plan.features) ? plan.features.join('\n') : '',
  highlight:     plan.highlight,
  is_active:     plan.is_active,
  sort_order:    plan.sort_order,
  color:         plan.color  || 'from-gray-400 to-gray-600',
  badge:         plan.badge  || '',
});
const formToPayload = (form) => ({
  ...form,
  storage_mb: Number(form.storage_mb),
  price_usd:  parseFloat(Number(form.price_usd).toFixed(2)),
  sort_order: Number(form.sort_order),
  features:   form.features.split('\n').map(f => f.trim()).filter(Boolean),
});

// ── ConversionBadge ───────────────────────────────────────────────────────────
function ConversionBadge({ usdAmount, variant = 'dark', showRate = true, className = '' }) {
  const { currency, currencyDetails, getExchangeRate, ratesReady } = useCurrency();
  const [hovered, setHovered] = useState(false);
  if (currency === 'USD' || !usdAmount || usdAmount === 0) return null;
  const rate = getExchangeRate('USD', currency);
  const rateStr = rate >= 1000
    ? rate.toLocaleString(undefined, { maximumFractionDigits: 0 })
    : rate.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 });
  const styles = {
    light: { pill: 'bg-white/20 text-white border-white/30',                    dot: ratesReady ? 'bg-green-400'  : 'bg-amber-400', tip: 'bg-white text-accent-navy'   },
    dark:  { pill: 'bg-accent-navy/8 text-accent-navy border-accent-navy/20',   dot: ratesReady ? 'bg-green-500'  : 'bg-amber-400', tip: 'bg-accent-navy text-white'   },
    amber: { pill: 'bg-amber-100 text-amber-800 border-amber-300',              dot: ratesReady ? 'bg-green-500'  : 'bg-amber-500', tip: 'bg-amber-800 text-white'     },
  };
  const s = styles[variant] || styles.dark;
  return (
    <span
      className={`relative inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border text-[9px] font-black uppercase tracking-widest select-none cursor-default ${s.pill} ${className}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <span className="relative flex-shrink-0 w-1.5 h-1.5">
        <span className={`absolute inset-0 rounded-full ${s.dot} ${ratesReady ? 'animate-ping opacity-60' : ''}`} />
        <span className={`relative block w-1.5 h-1.5 rounded-full ${s.dot}`} />
      </span>
      <span>~{currencyDetails?.symbol}{currency}</span>
      <AnimatePresence>
        {hovered && showRate && (
          <motion.span
            initial={{ opacity: 0, y: 4, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 4, scale: 0.95 }}
            transition={{ duration: 0.12 }}
            className={`absolute bottom-full left-1/2 -translate-x-1/2 mb-2 whitespace-nowrap px-3 py-1.5 rounded-xl text-[10px] font-bold shadow-xl z-50 pointer-events-none ${s.tip}`}
          >
            1 USD = {rateStr} {currency}
            <br />
            <span className="font-light opacity-80">{ratesReady ? '● Live exchange rate' : '◌ Estimated rate'}</span>
          </motion.span>
        )}
      </AnimatePresence>
    </span>
  );
}

function RateLine({ variant = 'dark' }) {
  const { currency, getExchangeRate, ratesReady } = useCurrency();
  if (currency === 'USD') return null;
  const rate = getExchangeRate('USD', currency);
  const rateStr = rate >= 1000
    ? rate.toLocaleString(undefined, { maximumFractionDigits: 0 })
    : rate.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 });
  const textCls = variant === 'light' ? 'text-white/45' : 'text-text-muted';
  return (
    <span className={`flex items-center gap-1 text-[9px] font-light ${textCls}`}>
      <SafeIcon icon={FiActivity} className="w-2.5 h-2.5 flex-shrink-0" />
      1 USD = {rateStr} {currency} ·{' '}
      <span className={`font-bold ${ratesReady ? (variant === 'light' ? 'text-green-300' : 'text-green-600') : 'text-amber-500'}`}>
        {ratesReady ? 'live rate' : 'est. rate'}
      </span>
    </span>
  );
}

function CurrencySwitcher() {
  const { currency, currencyDetails, supportedCurrencies, changeCurrency, ratesReady, getExchangeRate } = useCurrency();
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-2 px-4 py-2.5 bg-white/10 hover:bg-white/20 border border-white/20 rounded-2xl text-white text-[10px] font-black uppercase tracking-widest transition-all"
      >
        <SafeIcon icon={FiGlobe} className="w-4 h-4" />
        <span>{currencyDetails?.symbol} {currency}</span>
        <span className="relative w-2 h-2 flex-shrink-0">
          <span className={`absolute inset-0 rounded-full ${ratesReady ? 'bg-green-400 animate-ping opacity-60' : 'bg-amber-400'}`} />
          <span className={`relative block w-2 h-2 rounded-full ${ratesReady ? 'bg-green-400' : 'bg-amber-400'}`} />
        </span>
        <SafeIcon icon={FiChevronDown} className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -6, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.97 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 top-full mt-2 w-64 bg-white rounded-2xl shadow-2xl border border-brand-dark z-50 overflow-hidden"
          >
            <div className="px-4 py-2.5 border-b border-brand-dark">
              <p className="text-[9px] font-black uppercase tracking-widest text-text-muted">Display Currency</p>
              <p className="text-[10px] text-text-muted font-light mt-0.5">Prices stored in USD — converted at display time</p>
            </div>
            <div className="max-h-64 overflow-y-auto py-1">
              {supportedCurrencies.map(c => {
                const rate = getExchangeRate('USD', c.displayCode);
                const rateStr = rate >= 1000
                  ? rate.toLocaleString(undefined, { maximumFractionDigits: 0 })
                  : rate.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 3 });
                return (
                  <button key={c.displayCode} onClick={() => { changeCurrency(c.displayCode); setOpen(false); }}
                    className={`w-full flex items-center justify-between px-4 py-2.5 text-left hover:bg-brand-light transition-colors ${currency === c.displayCode ? 'bg-accent-navy/5' : ''}`}>
                    <div className="flex items-center gap-2.5">
                      <span className="text-sm font-bold text-accent-navy w-8 text-right">{c.symbol}</span>
                      <div>
                        <p className="text-[11px] font-bold text-accent-navy">{c.displayCode}</p>
                        <p className="text-[9px] text-text-muted font-light">{c.name}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {c.displayCode !== 'USD' && <span className="text-[9px] text-text-muted font-mono">={rateStr}</span>}
                      {currency === c.displayCode && <SafeIcon icon={FiCheck} className="w-3.5 h-3.5 text-accent-gold" />}
                    </div>
                  </button>
                );
              })}
            </div>
            <div className={`px-4 py-2 border-t border-brand-dark ${ratesReady ? 'bg-green-50' : 'bg-amber-50'}`}>
              <p className={`text-[9px] font-bold flex items-center gap-1.5 ${ratesReady ? 'text-green-700' : 'text-amber-700'}`}>
                <span className={`w-1.5 h-1.5 rounded-full inline-block ${ratesReady ? 'bg-green-500' : 'bg-amber-500'}`} />
                {ratesReady ? 'Live exchange rates · updates hourly' : 'Using estimated fallback rates'}
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      {open && <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />}
    </div>
  );
}

// ── Plan Type Badge ───────────────────────────────────────────────────────────
function PlanTypeBadge({ plan }) {
  if (isWillPlan(plan)) {
    return (
      <span className="inline-flex items-center gap-1 text-[8px] px-2 py-0.5 bg-amber-100 text-amber-700 border border-amber-200 rounded-full font-black uppercase tracking-widest flex-shrink-0">
        <SafeIcon icon={FiFileText} className="w-2.5 h-2.5" /> Legal
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[8px] px-2 py-0.5 bg-indigo-100 text-indigo-700 border border-indigo-200 rounded-full font-black uppercase tracking-widest flex-shrink-0">
      <SafeIcon icon={FiHardDrive} className="w-2.5 h-2.5" /> Storage
    </span>
  );
}

// ── MAIN COMPONENT ────────────────────────────────────────────────────────────
export default function PricingSettings() {
  const { convertAmount, formatCurrency, currency, currencyDetails, ratesReady } = useCurrency();

  const [plans,       setPlans]       = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [toast,       setToast]       = useState(null);
  const [view,        setView]        = useState('grid');
  const [modal,       setModal]       = useState(null);
  const [confirm,     setConfirm]     = useState(null);
  const [saving,      setSaving]      = useState(false);
  const [form,        setForm]        = useState(emptyForm());
  const [formErrs,    setFormErrs]    = useState({});
  const [preview,     setPreview]     = useState(false);
  const [planTypeTab, setPlanTypeTab] = useState('all');  // 'all' | 'docuvault' | 'will'

  const toLocal  = useCallback((usd) => convertAmount(usd, 'USD'), [convertAmount]);
  const fmtLocal = useCallback((usd) => {
    if (usd === 0) return 'Free';
    return formatCurrency(toLocal(usd));
  }, [formatCurrency, toLocal]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminFetchAllPlans(ADMIN_TOKEN);
      setPlans(Array.isArray(data) ? data.sort((a, b) => a.sort_order - b.sort_order) : []);
    } catch (e) {
      flash(e.message || 'Failed to load plans', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const flash = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const f = (k, v) => {
    setForm(p => ({ ...p, [k]: v }));
    setFormErrs(p => ({ ...p, [k]: '' }));
  };

  const validate = () => {
    const errs = {};
    if (!form.plan_key.trim()) errs.plan_key = 'Plan key is required';
    if (!form.name.trim())     errs.name     = 'Name is required';
    if (Number(form.price_usd) < 0) errs.price_usd = 'Price cannot be negative';
    if (!form.features.trim()) errs.features  = 'Add at least one feature';
    setFormErrs(errs);
    return Object.keys(errs).length === 0;
  };

  const openCreate = (preset = null) => {
    setForm(preset ? { ...emptyForm(), ...preset } : emptyForm());
    setFormErrs({});
    setPreview(false);
    setModal('create');
  };

  const openEdit = (plan) => {
    setForm(planToForm(plan));
    setFormErrs({});
    setPreview(false);
    setModal(plan);
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const payload = formToPayload(form);
      if (modal === 'create') {
        await adminCreatePlan(payload, ADMIN_TOKEN);
        flash('Plan created successfully');
      } else {
        await adminUpdatePlan(modal.id, payload, ADMIN_TOKEN);
        flash('Plan updated successfully');
      }
      setModal(null);
      load();
    } catch (e) {
      flash(e.message || 'Save failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (plan) => {
    try {
      await adminTogglePlan(plan.id, ADMIN_TOKEN);
      flash(`"${plan.name}" ${plan.is_active ? 'deactivated' : 'activated'}`);
      load();
    } catch (e) { flash(e.message, 'error'); }
  };

  const handleDelete = async () => {
    const plan = confirm;
    setConfirm(null);
    try {
      await adminDeletePlan(plan.id, ADMIN_TOKEN);
      flash(`"${plan.name}" deleted`);
      load();
    } catch (e) { flash(e.message, 'error'); }
  };

  const handleDuplicate = (plan) => {
    setForm({ ...planToForm(plan), plan_key: plan.plan_key + '_copy', name: plan.name + ' (Copy)', is_active: false, sort_order: plan.sort_order + 1 });
    setFormErrs({});
    setPreview(false);
    setModal('create');
  };

  const handleSeed = async () => {
    setSaving(true);
    try {
      const res = await adminTriggerSeed(ADMIN_TOKEN);
      flash(res.message, res.error ? 'error' : 'success');
      load();
    } catch (e) {
      flash(e.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  // ── Filtered plans by type tab ────────────────────────────────────────────
  const activeTabMeta  = PLAN_TYPE_TABS.find(t => t.id === planTypeTab) || PLAN_TYPE_TABS[0];
  const filteredPlans  = plans.filter(activeTabMeta.filter);

  const docuPlans = plans.filter(isDocuPlan);
  const willPlans = plans.filter(isWillPlan);

  const activePlans    = filteredPlans.filter(p => p.is_active);
  const highlightPlan  = filteredPlans.find(p => p.highlight && p.is_active);
  const avgUSD         = activePlans.length ? activePlans.reduce((s, p) => s + p.price_usd, 0) / activePlans.length : 0;
  const avgLocal       = fmtLocal(avgUSD);
  const isNonUSD       = currency !== 'USD';

  return (
    <div className="space-y-8">

      {/* Hero Header */}
      <div className="bg-gradient-to-r from-accent-navy via-[#1a2a5e] to-accent-navy text-white rounded-[32px] p-8 flex items-center gap-6 border border-white/10 shadow-2xl relative overflow-hidden">
        <div className="absolute inset-0 opacity-5" style={{ backgroundImage: 'radial-gradient(circle,#fff 1px,transparent 1px)', backgroundSize: '28px 28px' }} />
        <div className="w-16 h-16 bg-accent-gold text-accent-navy rounded-2xl flex items-center justify-center shadow-xl flex-shrink-0 z-10">
          <SafeIcon icon={FiPackage} className="w-8 h-8" />
        </div>
        <div className="z-10 flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-1 flex-wrap">
            <h2 className="font-serif text-2xl">Pricing &amp; Plans</h2>
            <span className="text-[9px] px-3 py-1 bg-accent-gold text-accent-navy rounded-full font-black uppercase tracking-widest">Super Admin</span>
          </div>
          <p className="text-white/60 text-sm font-light max-w-2xl">
            Unified control for <strong className="text-white/80">DocuVault storage tiers</strong> and <strong className="text-white/80">Will &amp; Legacy plans</strong>. All prices stored in USD — converted to local currency at display time.
          </p>
        </div>
        <div className="flex items-center gap-3 z-10 flex-shrink-0 flex-wrap justify-end">
          <CurrencySwitcher />
          <button onClick={load} title="Refresh" className="p-3 bg-white/10 hover:bg-white/20 rounded-2xl transition-all">
            <SafeIcon icon={FiRefreshCw} className={`w-5 h-5 text-white ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button onClick={() => setView(v => v === 'grid' ? 'list' : 'grid')} className="p-3 bg-white/10 hover:bg-white/20 rounded-2xl transition-all">
            <SafeIcon icon={view === 'grid' ? FiList : FiGrid} className="w-5 h-5 text-white" />
          </button>
          <button onClick={() => openCreate()} className="flex items-center gap-2 px-5 py-3 bg-accent-gold text-accent-navy rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-accent-gold/90 transition-all shadow-lg">
            <SafeIcon icon={FiPlus} className="w-4 h-4" /> New Plan
          </button>
        </div>
      </div>

      {/* Currency notice */}
      <AnimatePresence>
        {isNonUSD && (
          <motion.div
            initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
            className="flex items-center gap-3 px-5 py-3.5 bg-amber-50 border border-amber-200 rounded-2xl"
          >
            <SafeIcon icon={FiGlobe} className="w-4 h-4 text-amber-600 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-xs text-amber-800 font-medium">
                Prices displayed in <strong>{currencyDetails?.name} ({currency})</strong> — converted from USD using{' '}
                <span className={`font-bold ${ratesReady ? 'text-green-700' : 'text-amber-700'}`}>
                  {ratesReady ? 'live exchange rates' : 'estimated rates'}
                </span>.
                All plans are stored and billed in USD.
              </p>
              <RateLine variant="amber" />
            </div>
            <span className={`flex-shrink-0 flex items-center gap-1.5 text-[9px] font-black uppercase tracking-widest px-2.5 py-1.5 rounded-xl border ${ratesReady ? 'bg-green-50 text-green-700 border-green-200' : 'bg-amber-200 text-amber-800 border-amber-300'}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${ratesReady ? 'bg-green-500 animate-pulse' : 'bg-amber-500'}`} />
              {ratesReady ? 'Live rates' : 'Est. rates'}
            </span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Plan Type Tabs */}
      <div className="flex gap-2 border-b border-brand-dark overflow-x-auto hide-scrollbar pb-0">
        {PLAN_TYPE_TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setPlanTypeTab(tab.id)}
            className={`relative flex items-center gap-2 px-5 py-3.5 text-[10px] font-black uppercase tracking-widest whitespace-nowrap transition-all ${
              planTypeTab === tab.id ? 'text-accent-gold' : 'text-text-muted hover:text-accent-navy'
            }`}
          >
            <SafeIcon icon={tab.icon} className="w-3.5 h-3.5" />
            {tab.label}
            {/* Count badge */}
            <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-black ${
              planTypeTab === tab.id ? 'bg-accent-gold text-white' : 'bg-brand-dark text-text-muted'
            }`}>
              {tab.id === 'all' ? plans.length : tab.id === 'docuvault' ? docuPlans.length : willPlans.length}
            </span>
            {planTypeTab === tab.id && (
              <motion.div layoutId="pricingTypeTab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-accent-gold rounded-t-full" />
            )}
          </button>
        ))}
      </div>

      {/* Tab description + source-of-truth info */}
      <div className="flex items-start gap-4 p-5 bg-blue-50 border border-blue-200 rounded-2xl">
        <SafeIcon icon={FiInfo} className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-sm font-bold text-blue-800 mb-1">
            {planTypeTab === 'will'
              ? 'Will & Legacy Plans — Source of Truth'
              : planTypeTab === 'docuvault'
              ? 'DocuVault Tiers — Source of Truth'
              : 'Unified Pricing — Source of Truth'}
          </p>
          <p className="text-xs text-blue-700 leading-relaxed">
            {planTypeTab === 'will'
              ? 'Changes here instantly sync to the Legal Will Prep pricing tables (LegalWillPrep.jsx + WillPricingModal). Prices are stored in USD and converted using live exchange rates when displayed to users.'
              : planTypeTab === 'docuvault'
              ? 'Changes here instantly sync to DocuVault upgrade flows and the Monetization → Storage panel. Storage limits (storage_mb) and prices are the single source of truth.'
              : 'This panel is the single source of truth for all pricing across the platform — both DocuVault storage tiers and Will & Legacy legal plans. Changes here propagate immediately to all user-facing pricing components.'}
            {' '}The <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-blue-100 rounded-full font-black text-[9px] uppercase tracking-widest border border-blue-200">~{currencyDetails?.symbol}{currency}</span> badge indicates a converted price — hover it to see the exact exchange rate used.
          </p>
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { icon: FiPackage,    label: planTypeTab === 'will' ? 'Will Plans' : planTypeTab === 'docuvault' ? 'Storage Tiers' : 'Total Plans', value: filteredPlans.length, sub: `${activePlans.length} active`, bg: 'bg-accent-navy', ic: 'text-white', showBadge: false },
          { icon: FiStar,       label: 'Featured Plan',  value: highlightPlan?.name || 'None', sub: 'highlighted tier',                bg: 'bg-accent-gold', ic: 'text-accent-navy', showBadge: false },
          { icon: FiDollarSign, label: 'Avg Price',      value: avgUSD === 0 ? 'Free' : avgLocal, sub: 'across active plans · USD base', bg: 'bg-green-500',   ic: 'text-white',       showBadge: true  },
          { icon: FiFilter,     label: 'Plan Types',     value: `${docuPlans.length}+${willPlans.length}`, sub: 'Storage + Legal plans', bg: 'bg-purple-600',  ic: 'text-white',       showBadge: false },
        ].map(s => (
          <div key={s.label} className="bg-white border border-brand-dark rounded-[24px] p-5 flex items-start gap-4">
            <div className={`p-3 rounded-xl ${s.bg} flex-shrink-0`}>
              <SafeIcon icon={s.icon} className={`w-5 h-5 ${s.ic}`} />
            </div>
            <div className="min-w-0">
              <p className="text-[10px] font-black uppercase tracking-widest text-text-muted">{s.label}</p>
              <div className="flex items-center gap-2 flex-wrap mt-0.5">
                <p className="text-xl font-serif text-accent-navy truncate">{s.value}</p>
                {s.showBadge && avgUSD > 0 && <ConversionBadge usdAmount={avgUSD} variant="dark" showRate />}
              </div>
              <p className="text-[10px] text-text-muted">{s.sub}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Plan List / Grid */}
      {loading ? (
        <LoadingSkeleton />
      ) : filteredPlans.length === 0 ? (
        <EmptyState planTypeTab={planTypeTab} onAdd={() => openCreate()} onTemplate={(preset) => openCreate(preset)} onSeed={handleSeed} />
      ) : view === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {filteredPlans.map(plan => (
            <PlanCard
              key={plan.id} plan={plan}
              fmtLocal={fmtLocal} toLocal={toLocal}
              onEdit={() => openEdit(plan)}
              onToggle={() => handleToggle(plan)}
              onDelete={() => setConfirm(plan)}
              onDuplicate={() => handleDuplicate(plan)}
            />
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-[28px] border border-brand-dark overflow-hidden">
          <div className="grid grid-cols-[1fr_auto_auto_auto_auto_auto] gap-0 px-6 py-3 border-b border-brand-dark">
            {['Plan', 'Type', 'Storage', 'Price', 'Status', 'Actions'].map(h => (
              <span key={h} className="text-[9px] font-black uppercase tracking-widest text-text-muted">{h}</span>
            ))}
          </div>
          <div className="divide-y divide-brand-dark">
            {filteredPlans.map(plan => (
              <PlanListRow
                key={plan.id} plan={plan}
                fmtLocal={fmtLocal} toLocal={toLocal}
                onEdit={() => openEdit(plan)}
                onToggle={() => handleToggle(plan)}
                onDelete={() => setConfirm(plan)}
                onDuplicate={() => handleDuplicate(plan)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Modals */}
      <AnimatePresence>
        {modal && (
          <PlanFormModal
            isCreate={modal === 'create'}
            form={form} setF={f} formErrs={formErrs}
            preview={preview} setPreview={setPreview}
            saving={saving} fmtLocal={fmtLocal} toLocal={toLocal}
            onSave={handleSave} onClose={() => setModal(null)}
          />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {confirm && <DeleteConfirmModal plan={confirm} onConfirm={handleDelete} onCancel={() => setConfirm(null)} />}
      </AnimatePresence>

      {/* Toast */}
      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: 20, x: '-50%' }}
            animate={{ opacity: 1, y: 0, x: '-50%' }}
            exit={{ opacity: 0, y: 20, x: '-50%' }}
            className={`fixed bottom-8 left-1/2 z-[9999] flex items-center gap-3 px-6 py-4 rounded-2xl shadow-2xl text-white text-sm font-bold ${toast.type === 'error' ? 'bg-red-600' : 'bg-green-600'}`}
          >
            <SafeIcon icon={toast.type === 'error' ? FiAlertTriangle : FiCheck} className="w-4 h-4" />
            {toast.msg}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ── Empty State ───────────────────────────────────────────────────────────────
function EmptyState({ planTypeTab, onAdd, onTemplate, onSeed }) {
  const relevantTemplates = planTypeTab === 'will'
    ? STARTER_TEMPLATES.filter(t => t.preset.plan_key.startsWith('will_'))
    : planTypeTab === 'docuvault'
    ? STARTER_TEMPLATES.filter(t => !t.preset.plan_key.startsWith('will_'))
    : STARTER_TEMPLATES;

  return (
    <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-[32px] border-2 border-dashed border-brand-dark overflow-hidden">
      <div className="bg-gradient-to-br from-accent-navy via-[#1a2a5e] to-accent-navy px-8 pt-12 pb-8 text-center relative overflow-hidden">
        <div className="absolute inset-0 opacity-5" style={{ backgroundImage: 'radial-gradient(circle,#fff 1px,transparent 1px)', backgroundSize: '24px 24px' }} />
        <h3 className="font-serif text-white text-2xl mb-2 relative z-10">No Plans in This Category</h3>
        <p className="text-white/60 text-sm font-light max-w-md mx-auto relative z-10">
          {planTypeTab === 'will'
            ? 'No Will & Legacy plans exist yet. Create them here to power the Legal Will Prep pricing tables.'
            : planTypeTab === 'docuvault'
            ? 'No DocuVault storage tiers exist yet. Create them to enable the upgrade flow.'
            : 'No plans configured yet. Create your first plan or pick a template below.'}
        </p>
      </div>
      <div className="px-8 py-8 space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {relevantTemplates.map((tpl, i) => (
            <motion.button key={tpl.label} initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.1 * i }} whileHover={{ y: -3, scale: 1.02 }} whileTap={{ scale: 0.97 }}
              onClick={() => onTemplate(tpl.preset)}
              className={`group text-left p-4 rounded-2xl border-2 ${tpl.border} ${tpl.bg} hover:shadow-md transition-all`}>
              <div className={`w-full h-10 rounded-xl bg-gradient-to-br ${tpl.color} mb-3 opacity-80 group-hover:opacity-100 transition-opacity`} />
              <p className="text-xs font-black text-accent-navy">{tpl.label}</p>
              <p className="text-[10px] text-text-muted font-light mt-0.5 leading-snug">{tpl.description}</p>
            </motion.button>
          ))}
        </div>
        <div className="flex justify-center gap-4 flex-wrap">
          <button onClick={onSeed}
            className="flex items-center gap-2.5 px-8 py-4 bg-white text-accent-navy border-2 border-brand-dark rounded-2xl text-[11px] font-black uppercase tracking-widest hover:border-accent-navy transition-all shadow-sm">
            <SafeIcon icon={FiZap} className="w-4 h-4" /> Seed Default Plans
          </button>
          <button onClick={onAdd}
            className="flex items-center gap-2.5 px-8 py-4 bg-accent-navy text-white rounded-2xl text-[11px] font-black uppercase tracking-widest hover:bg-accent-navy/90 transition-all shadow-xl shadow-accent-navy/20">
            <SafeIcon icon={FiPlus} className="w-4 h-4" /> Create New Plan
          </button>
        </div>
      </div>
    </motion.div>
  );
}

// ── Plan Card ─────────────────────────────────────────────────────────────────
function PlanCard({ plan, fmtLocal, toLocal, onEdit, onToggle, onDelete, onDuplicate }) {
  const [expanded, setExpanded] = useState(false);
  const billingLabel = BILLING_LABELS[plan.billing_cycle] ?? '';
  const { currency } = useCurrency();
  const isNonUSD = currency !== 'USD';
  const willPlan = isWillPlan(plan);

  return (
    <motion.div layout initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
      className={`bg-white rounded-[28px] border-2 overflow-hidden transition-all ${
        plan.is_active
          ? willPlan ? 'border-amber-200 hover:border-amber-400/60' : 'border-brand-dark hover:border-accent-gold/40'
          : 'border-dashed border-brand-dark opacity-60'
      }`}>
      <div className={`bg-gradient-to-br ${plan.color || 'from-gray-400 to-gray-600'} p-6 relative`}>
        {/* Type badge */}
        <div className="absolute top-3 left-3">
          {willPlan
            ? <span className="text-[8px] px-2 py-0.5 bg-amber-400 text-white rounded-full font-black uppercase tracking-widest">Legal</span>
            : <span className="text-[8px] px-2 py-0.5 bg-indigo-400 text-white rounded-full font-black uppercase tracking-widest">Storage</span>
          }
        </div>

        <div className="flex items-start justify-between mt-4">
          <div>
            <p className="text-white/70 text-[10px] font-black uppercase tracking-widest">{plan.plan_key}</p>
            <h4 className="text-white font-serif text-2xl mt-0.5">{plan.name}</h4>
            {plan.description && <p className="text-white/60 text-xs mt-1 font-light">{plan.description}</p>}
          </div>
          <div className="flex flex-col items-end gap-2">
            {plan.highlight && (
              <span className="flex items-center gap-1 text-[9px] px-2 py-1 bg-white/20 text-white rounded-full font-black uppercase tracking-widest">
                <SafeIcon icon={FiStar} className="w-3 h-3" /> Popular
              </span>
            )}
            {!plan.is_active && (
              <span className="text-[9px] px-2 py-1 bg-black/20 text-white rounded-full font-black uppercase tracking-widest">Inactive</span>
            )}
          </div>
        </div>

        {/* Price block */}
        <div className="mt-5">
          {plan.price_usd === 0 ? (
            <span className="text-4xl font-serif text-white">Free</span>
          ) : (
            <>
              <div className="flex items-baseline gap-2 flex-wrap">
                <span className="text-4xl font-serif text-white">
                  {isNonUSD ? toLocal(plan.price_usd).toLocaleString(undefined, { maximumFractionDigits: 2 }) : plan.price_usd.toFixed(2)}
                </span>
                <span className="text-white/60 text-sm font-light">{billingLabel}</span>
                <ConversionBadge usdAmount={plan.price_usd} variant="light" showRate />
              </div>
              <div className="mt-1.5 space-y-0.5">
                {isNonUSD && <p className="text-white/50 text-[10px] font-light">${plan.price_usd.toFixed(2)} USD</p>}
                <RateLine variant="light" />
              </div>
            </>
          )}
        </div>

        {!willPlan && (
          <div className="mt-3 flex items-center gap-2">
            <SafeIcon icon={FiHardDrive} className="w-3.5 h-3.5 text-white/60" />
            <span className="text-white/70 text-xs">{formatStorage(plan.storage_mb)} storage</span>
          </div>
        )}
      </div>

      <div className="p-5 space-y-4">
        <div>
          <button onClick={() => setExpanded(e => !e)} className="flex items-center justify-between w-full text-left group">
            <span className="text-[10px] font-black uppercase tracking-widest text-text-muted group-hover:text-accent-navy transition-colors">
              {plan.features?.length || 0} Features
            </span>
            <SafeIcon icon={expanded ? FiChevronUp : FiChevronDown} className="w-4 h-4 text-text-muted" />
          </button>
          <AnimatePresence>
            {expanded && (
              <motion.ul initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden mt-3 space-y-1.5">
                {(plan.features || []).map((feat, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-text-muted">
                    <SafeIcon icon={FiCheck} className="w-3.5 h-3.5 text-green-500 flex-shrink-0 mt-0.5" />
                    {feat}
                  </li>
                ))}
              </motion.ul>
            )}
          </AnimatePresence>
        </div>
        <div className="flex items-center gap-2 pt-2 border-t border-brand-light">
          <button onClick={onEdit} className="flex-1 flex items-center justify-center gap-1.5 py-2.5 bg-accent-navy text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-accent-navy/90 transition-all">
            <SafeIcon icon={FiEdit2} className="w-3.5 h-3.5" /> Edit
          </button>
          <button onClick={onToggle} title={plan.is_active ? 'Deactivate' : 'Activate'}
            className={`w-10 h-10 rounded-xl flex items-center justify-center transition-colors flex-shrink-0 ${plan.is_active ? 'bg-green-50 text-green-500 hover:bg-green-100' : 'bg-gray-100 text-gray-400 hover:bg-gray-200'}`}>
            <SafeIcon icon={plan.is_active ? FiToggleRight : FiToggleLeft} className="w-4 h-4" />
          </button>
          <button onClick={onDuplicate} title="Duplicate" className="w-10 h-10 bg-blue-50 text-blue-500 hover:bg-blue-100 rounded-xl flex items-center justify-center transition-colors flex-shrink-0">
            <SafeIcon icon={FiCopy} className="w-3.5 h-3.5" />
          </button>
          <button onClick={onDelete} title="Delete" className="w-10 h-10 bg-red-50 text-red-400 hover:bg-red-100 rounded-xl flex items-center justify-center transition-colors flex-shrink-0">
            <SafeIcon icon={FiTrash2} className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </motion.div>
  );
}

// ── Plan List Row ─────────────────────────────────────────────────────────────
function PlanListRow({ plan, fmtLocal, toLocal, onEdit, onToggle, onDelete, onDuplicate }) {
  const billingLabel = BILLING_LABELS[plan.billing_cycle] ?? '';
  const { currency } = useCurrency();
  const isNonUSD = currency !== 'USD';
  const willPlan = isWillPlan(plan);

  return (
    <div className={`grid grid-cols-[1fr_auto_auto_auto_auto_auto] items-center gap-4 px-6 py-4 hover:bg-brand-light/50 transition-colors ${!plan.is_active ? 'opacity-50' : ''}`}>
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-bold text-accent-navy text-sm">{plan.name}</span>
          <span className="text-[9px] font-black uppercase tracking-widest bg-brand-light text-text-muted px-2 py-0.5 rounded-full border border-brand-dark">{plan.plan_key}</span>
          {plan.highlight && <SafeIcon icon={FiStar} className="w-3.5 h-3.5 text-accent-gold" />}
        </div>
        <p className="text-[11px] text-text-muted mt-0.5 truncate">{plan.description || `${plan.features?.length || 0} features`}</p>
      </div>
      {/* Type badge */}
      <PlanTypeBadge plan={plan} />
      {/* Storage (only for DocuVault) */}
      <span className="text-sm font-bold text-accent-navy whitespace-nowrap">
        {willPlan ? '—' : formatStorage(plan.storage_mb)}
      </span>
      {/* Price */}
      <div className="text-right">
        <div className="flex items-center justify-end gap-1.5 flex-wrap">
          <p className="text-sm font-bold text-accent-navy whitespace-nowrap">
            {plan.price_usd === 0 ? 'Free' : `${fmtLocal(plan.price_usd)}${billingLabel}`}
          </p>
          {plan.price_usd > 0 && <ConversionBadge usdAmount={plan.price_usd} variant="dark" showRate />}
        </div>
        {isNonUSD && plan.price_usd > 0 && (
          <div className="mt-0.5">
            <p className="text-[9px] text-text-muted">${plan.price_usd.toFixed(2)} USD</p>
            <RateLine variant="dark" />
          </div>
        )}
      </div>
      <span className={`text-[9px] font-black uppercase tracking-widest px-2 py-1 rounded-full ${plan.is_active ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-400'}`}>
        {plan.is_active ? 'Active' : 'Inactive'}
      </span>
      <div className="flex items-center gap-1.5">
        <button onClick={onEdit}      className="w-8 h-8 bg-accent-navy/10 text-accent-navy hover:bg-accent-navy hover:text-white rounded-xl flex items-center justify-center transition-all"><SafeIcon icon={FiEdit2}       className="w-3.5 h-3.5" /></button>
        <button onClick={onToggle}    className={`w-8 h-8 rounded-xl flex items-center justify-center transition-colors ${plan.is_active ? 'bg-green-50 text-green-500 hover:bg-green-100' : 'bg-gray-100 text-gray-400 hover:bg-gray-200'}`}><SafeIcon icon={plan.is_active ? FiToggleRight : FiToggleLeft} className="w-4 h-4" /></button>
        <button onClick={onDuplicate} className="w-8 h-8 bg-blue-50 text-blue-500 hover:bg-blue-100 rounded-xl flex items-center justify-center transition-colors"><SafeIcon icon={FiCopy}    className="w-3.5 h-3.5" /></button>
        <button onClick={onDelete}    className="w-8 h-8 bg-red-50 text-red-400 hover:bg-red-100 rounded-xl flex items-center justify-center transition-colors"><SafeIcon icon={FiTrash2} className="w-3.5 h-3.5" /></button>
      </div>
    </div>
  );
}

// ── Plan Form Modal ───────────────────────────────────────────────────────────
function PlanFormModal({ isCreate, form, setF, formErrs, preview, setPreview, saving, fmtLocal, toLocal, onSave, onClose }) {
  const { currency, ratesReady, currencyDetails, getExchangeRate } = useCurrency();
  const isNonUSD = currency !== 'USD';
  const priceNum = Number(form.price_usd);
  const isWill   = form.plan_key?.startsWith('will_') || false;

  const rate = getExchangeRate('USD', currency);
  const rateStr = rate >= 1000
    ? rate.toLocaleString(undefined, { maximumFractionDigits: 0 })
    : rate.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 });

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
      <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white rounded-[32px] w-full max-w-2xl shadow-2xl my-4">

        <div className="flex items-center justify-between px-8 pt-8 pb-6 border-b border-brand-dark">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-2xl flex items-center justify-center ${isWill ? 'bg-amber-50 text-amber-600' : 'bg-accent-gold/10 text-accent-gold'}`}>
              <SafeIcon icon={isCreate ? FiPlus : FiEdit2} className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-serif text-accent-navy text-lg">{isCreate ? 'Create New Plan' : `Edit: ${form.name || 'Plan'}`}</h3>
                {isWill
                  ? <span className="text-[8px] px-2 py-0.5 bg-amber-100 text-amber-700 border border-amber-200 rounded-full font-black uppercase tracking-widest">Legal Plan</span>
                  : form.plan_key && <span className="text-[8px] px-2 py-0.5 bg-indigo-100 text-indigo-700 border border-indigo-200 rounded-full font-black uppercase tracking-widest">Storage Tier</span>
                }
              </div>
              <p className="text-[10px] text-text-muted font-light">All fields marked * are required</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setPreview(p => !p)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest border transition-all ${preview ? 'bg-accent-navy text-white border-accent-navy' : 'border-brand-dark text-text-muted hover:border-accent-navy'}`}>
              <SafeIcon icon={preview ? FiEyeOff : FiEye} className="w-3.5 h-3.5" />
              {preview ? 'Edit' : 'Preview'}
            </button>
            <button onClick={onClose} className="w-9 h-9 bg-brand-light hover:bg-red-50 hover:text-red-500 rounded-xl flex items-center justify-center transition-colors text-text-muted">
              <SafeIcon icon={FiX} className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="px-8 py-6 max-h-[70vh] overflow-y-auto">
          <AnimatePresence mode="wait">
            {preview ? (
              <motion.div key="preview" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <PlanPreview form={form} fmtLocal={fmtLocal} toLocal={toLocal} />
              </motion.div>
            ) : (
              <motion.div key="form" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-6">

                <FormSection title="Identity" icon={FiTag}>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <FormField label="Plan Key *" error={formErrs.plan_key} hint={isCreate ? 'Lowercase slug. Use will_ prefix for legal plans, e.g. will_draft' : 'Cannot be changed after creation'}>
                      <input value={form.plan_key} onChange={e => setF('plan_key', e.target.value.toLowerCase().replace(/\s+/g, '_'))}
                        disabled={!isCreate} placeholder="e.g. will_guardian or family"
                        className={`input-field ${!isCreate ? 'opacity-50 cursor-not-allowed' : ''} ${formErrs.plan_key ? 'border-red-400' : ''}`} />
                    </FormField>
                    <FormField label="Display Name *" error={formErrs.name}>
                      <input value={form.name} onChange={e => setF('name', e.target.value)}
                        placeholder="e.g. Legacy Guardian" className={`input-field ${formErrs.name ? 'border-red-400' : ''}`} />
                    </FormField>
                    <div className="sm:col-span-2">
                      <FormField label="Description">
                        <input value={form.description} onChange={e => setF('description', e.target.value)}
                          placeholder="Short tagline shown to users" className="input-field" />
                      </FormField>
                    </div>
                  </div>
                  {/* Plan type auto-detection notice */}
                  {form.plan_key && (
                    <div className={`flex items-center gap-2 p-3 rounded-xl border text-xs font-medium ${
                      form.plan_key.startsWith('will_')
                        ? 'bg-amber-50 border-amber-200 text-amber-800'
                        : 'bg-indigo-50 border-indigo-200 text-indigo-800'
                    }`}>
                      <SafeIcon icon={form.plan_key.startsWith('will_') ? FiFileText : FiHardDrive} className="w-4 h-4 flex-shrink-0" />
                      {form.plan_key.startsWith('will_')
                        ? '✓ This will be a Legal / Will plan — it will appear in the Legal Will Prep pricing tables.'
                        : '✓ This will be a DocuVault storage tier — it will appear in the upgrade flow and Monetization panel.'}
                    </div>
                  )}
                </FormSection>

                <FormSection title="Pricing & Billing" icon={FiDollarSign}>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <FormField label="Price (USD) *" error={formErrs.price_usd}>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted font-bold">$</span>
                        <input type="number" min="0" step="0.01" value={form.price_usd}
                          onChange={e => setF('price_usd', e.target.value)}
                          className={`input-field pl-7 ${formErrs.price_usd ? 'border-red-400' : ''}`} />
                      </div>
                      {isNonUSD && priceNum > 0 && (
                        <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-xl space-y-1.5">
                          <div className="flex items-center gap-2 flex-wrap">
                            <SafeIcon icon={FiGlobe} className="w-3 h-3 text-amber-600 flex-shrink-0" />
                            <span className="text-[11px] text-amber-900 font-bold">≈ {fmtLocal(priceNum)}</span>
                            <ConversionBadge usdAmount={priceNum} variant="amber" showRate />
                          </div>
                          <div className="flex items-center gap-1.5 text-[9px] text-amber-700">
                            <SafeIcon icon={FiActivity} className="w-2.5 h-2.5 flex-shrink-0" />
                            <span>1 USD = {rateStr} {currency}</span>
                            <span className={`font-black px-1.5 py-0.5 rounded-full ${ratesReady ? 'bg-green-100 text-green-700' : 'bg-amber-200 text-amber-800'}`}>
                              {ratesReady ? '● live' : '◌ est.'}
                            </span>
                          </div>
                          <p className="text-[9px] text-amber-600 font-light">Stored &amp; billed in USD — converted at display time</p>
                        </div>
                      )}
                      {!isNonUSD && <p className="text-[10px] text-text-muted mt-1">Stored in USD · displayed in local currency at runtime</p>}
                    </FormField>

                    <FormField label="Billing Cycle">
                      <select value={form.billing_cycle} onChange={e => setF('billing_cycle', e.target.value)} className="input-field">
                        {BILLING_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    </FormField>

                    <FormField label="Storage (MB)" hint={isWill ? 'Set 0 for non-storage plans' : 'Required for DocuVault tiers'} error={formErrs.storage_mb}>
                      <div className="relative">
                        <input type="number" min="0" value={form.storage_mb}
                          onChange={e => setF('storage_mb', e.target.value)}
                          className={`input-field pr-14 ${formErrs.storage_mb ? 'border-red-400' : ''}`} />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-black text-text-muted">MB</span>
                      </div>
                    </FormField>
                  </div>
                  {!isWill && (
                    <div className="flex flex-wrap gap-2 mt-1">
                      {[100, 512, 1024, 5120, 10240, 20480].map(mb => (
                        <button key={mb} type="button" onClick={() => setF('storage_mb', mb)}
                          className={`px-3 py-1.5 rounded-xl text-[9px] font-black uppercase tracking-widest border transition-all ${Number(form.storage_mb) === mb ? 'bg-accent-navy text-white border-accent-navy' : 'border-brand-dark text-text-muted hover:border-accent-navy'}`}>
                          {formatStorage(mb)}
                        </button>
                      ))}
                    </div>
                  )}
                </FormSection>

                <FormSection title="Features *" icon={FiList}>
                  <FormField label="One feature per line" error={formErrs.features}>
                    <textarea rows={6} value={form.features} onChange={e => setF('features', e.target.value)}
                      placeholder={isWill
                        ? 'Full AI-guided 4-step interview\nJurisdiction-aware legal language\nDownloadable PDF will draft\nExecutor & beneficiary clauses'
                        : '1 GB secure storage\nUnlimited documents\nAES-256 encryption\nPriority support'}
                      className={`input-field resize-none font-light text-sm leading-relaxed ${formErrs.features ? 'border-red-400' : ''}`} />
                  </FormField>
                  <p className="text-[10px] text-text-muted">{form.features.split('\n').filter(f => f.trim()).length} feature(s) defined</p>
                </FormSection>

                <FormSection title="Appearance" icon={FiSliders}>
                  <div>
                    <p className="text-[10px] font-black uppercase tracking-widest text-text-muted mb-3">Card Gradient</p>
                    <div className="flex flex-wrap gap-3">
                      {COLOR_PRESETS.map(cp => (
                        <button key={cp.value} type="button" onClick={() => setF('color', cp.value)}
                          className={`relative w-14 h-10 rounded-xl bg-gradient-to-br ${cp.value} transition-all ${form.color === cp.value ? 'ring-2 ring-offset-2 ring-accent-gold scale-110' : 'hover:scale-105'}`}
                          title={cp.label}>
                          {form.color === cp.value && <SafeIcon icon={FiCheck} className="absolute inset-0 m-auto w-4 h-4 text-white drop-shadow" />}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <FormField label="Sort Order">
                      <div className="flex items-center gap-2">
                        <input type="number" min="0" value={form.sort_order} onChange={e => setF('sort_order', e.target.value)} className="input-field" />
                        <div className="flex flex-col gap-1">
                          <button type="button" onClick={() => setF('sort_order', Math.max(0, Number(form.sort_order) - 1))} className="p-1 bg-brand-light rounded-lg hover:bg-brand-dark transition-colors"><SafeIcon icon={FiArrowUp}   className="w-3 h-3 text-text-muted" /></button>
                          <button type="button" onClick={() => setF('sort_order', Number(form.sort_order) + 1)}              className="p-1 bg-brand-light rounded-lg hover:bg-brand-dark transition-colors"><SafeIcon icon={FiArrowDown} className="w-3 h-3 text-text-muted" /></button>
                        </div>
                      </div>
                    </FormField>
                    <FormField label="Badge Text">
                      <input value={form.badge} onChange={e => setF('badge', e.target.value)}
                        placeholder="e.g. Most Popular" className="input-field" />
                    </FormField>
                  </div>
                  <div className="flex items-center gap-6 mt-2">
                    <ToggleField label="Highlight as Popular" desc="Shows a 'Popular' badge on this plan" value={form.highlight} onChange={v => setF('highlight', v)} />
                    <ToggleField label="Active" desc="Visible to users in pricing flows" value={form.is_active} onChange={v => setF('is_active', v)} />
                  </div>
                </FormSection>

              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <div className="flex justify-end gap-3 px-8 py-6 border-t border-brand-dark">
          <button onClick={onClose} className="flex items-center gap-2 px-6 py-3 border-2 border-brand-dark text-text-muted rounded-2xl text-[10px] font-black uppercase tracking-widest hover:border-accent-navy hover:text-accent-navy transition-all">
            <SafeIcon icon={FiX} className="w-3.5 h-3.5" /> Cancel
          </button>
          <button onClick={onSave} disabled={saving}
            className="flex items-center gap-2 px-8 py-3 bg-accent-navy text-white rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-accent-navy/90 transition-all shadow-lg disabled:opacity-60 disabled:cursor-not-allowed">
            {saving ? <SafeIcon icon={FiLoader} className="w-4 h-4 animate-spin" /> : <SafeIcon icon={FiSave} className="w-4 h-4" />}
            {saving ? 'Saving…' : isCreate ? 'Create Plan' : 'Save Changes'}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ── Plan Preview ──────────────────────────────────────────────────────────────
function PlanPreview({ form, fmtLocal, toLocal }) {
  const billingLabel = BILLING_LABELS[form.billing_cycle] ?? '';
  const { currency } = useCurrency();
  const features = form.features.split('\n').map(f => f.trim()).filter(Boolean);
  const priceUSD = Number(form.price_usd);
  const isNonUSD = currency !== 'USD';

  return (
    <div className="flex justify-center py-4">
      <div className={`w-72 rounded-[28px] overflow-hidden border-2 ${form.highlight ? 'border-accent-gold shadow-xl shadow-accent-gold/20' : 'border-brand-dark'}`}>
        <div className={`bg-gradient-to-br ${form.color || 'from-gray-400 to-gray-600'} p-6`}>
          {form.highlight && (
            <div className="flex justify-end mb-2">
              <span className="text-[9px] px-2 py-1 bg-white/20 text-white rounded-full font-black uppercase tracking-widest flex items-center gap-1">
                <SafeIcon icon={FiStar} className="w-2.5 h-2.5" /> Popular
              </span>
            </div>
          )}
          <p className="text-white/70 text-[10px] font-black uppercase tracking-widest">{form.plan_key || 'plan_key'}</p>
          <h4 className="text-white font-serif text-2xl mt-0.5">{form.name || 'Plan Name'}</h4>
          {form.description && <p className="text-white/60 text-xs mt-1 font-light">{form.description}</p>}
          <div className="mt-5">
            {priceUSD === 0 ? (
              <span className="text-3xl font-serif text-white">Free</span>
            ) : (
              <>
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span className="text-3xl font-serif text-white">
                    {isNonUSD ? toLocal(priceUSD).toLocaleString(undefined, { maximumFractionDigits: 2 }) : priceUSD.toFixed(2)}
                  </span>
                  <span className="text-white/60 text-sm font-light">{billingLabel}</span>
                  <ConversionBadge usdAmount={priceUSD} variant="light" showRate={false} />
                </div>
                <div className="mt-1 space-y-0.5">
                  {isNonUSD && <p className="text-white/50 text-[10px]">${priceUSD.toFixed(2)} USD</p>}
                  <RateLine variant="light" />
                </div>
              </>
            )}
          </div>
          {form.storage_mb > 0 && (
            <div className="mt-2 flex items-center gap-2">
              <SafeIcon icon={FiHardDrive} className="w-3.5 h-3.5 text-white/60" />
              <span className="text-white/70 text-xs">{formatStorage(Number(form.storage_mb) || 0)} storage</span>
            </div>
          )}
        </div>
        <div className="bg-white p-5 space-y-2">
          {features.length === 0 ? (
            <p className="text-text-muted text-xs text-center py-4 font-light italic">No features added yet</p>
          ) : features.map((feat, i) => (
            <div key={i} className="flex items-start gap-2 text-xs text-text-muted">
              <SafeIcon icon={FiCheck} className="w-3.5 h-3.5 text-green-500 flex-shrink-0 mt-0.5" />
              {feat}
            </div>
          ))}
          <button className="w-full mt-4 py-3 bg-accent-navy text-white rounded-2xl text-[10px] font-black uppercase tracking-widest">Get Started</button>
        </div>
      </div>
    </div>
  );
}

// ── Delete Confirm Modal ──────────────────────────────────────────────────────
function DeleteConfirmModal({ plan, onConfirm, onCancel }) {
  const isSystem = ['free','starter','family','legacy','will_draft','will_guardian','will_estate'].includes(plan.plan_key);
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }}
        className="bg-white rounded-[32px] p-8 max-w-sm w-full shadow-2xl">
        <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-5 ${isSystem ? 'bg-amber-50' : 'bg-red-50'}`}>
          <SafeIcon icon={isSystem ? FiLock : FiTrash2} className={`w-8 h-8 ${isSystem ? 'text-amber-500' : 'text-red-500'}`} />
        </div>
        <h4 className="text-xl font-serif text-accent-navy text-center mb-2">
          {isSystem ? 'Deactivate Plan?' : 'Delete Plan?'}
        </h4>
        <p className="text-sm text-text-muted text-center mb-2 font-light">
          You are about to {isSystem ? 'deactivate' : 'permanently delete'}{' '}
          <strong className="text-accent-navy">"{plan.name}"</strong>.
        </p>
        {isSystem && (
          <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-xl p-3 text-center mb-4">
            System plans cannot be permanently deleted. They will be deactivated and hidden from users.
          </p>
        )}
        {!isSystem && (
          <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-xl p-3 text-center mb-4">
            This action is irreversible. The plan will be permanently removed from the database.
          </p>
        )}
        <div className="flex gap-3">
          <button onClick={onCancel} className="flex-1 py-3 border-2 border-brand-dark text-text-muted rounded-2xl text-sm font-bold hover:border-accent-navy transition-colors">Cancel</button>
          <button onClick={onConfirm} className={`flex-1 py-3 text-white rounded-2xl text-sm font-bold transition-colors ${isSystem ? 'bg-amber-500 hover:bg-amber-600' : 'bg-red-500 hover:bg-red-600'}`}>
            {isSystem ? 'Deactivate' : 'Delete'}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ── Small Helpers ─────────────────────────────────────────────────────────────
const FormSection = ({ title, icon, children }) => (
  <div className="space-y-4">
    <div className="flex items-center gap-2 pb-2 border-b border-brand-light">
      <SafeIcon icon={icon} className="w-4 h-4 text-accent-gold" />
      <span className="text-[10px] font-black uppercase tracking-widest text-accent-navy">{title}</span>
    </div>
    {children}
  </div>
);

const FormField = ({ label, error, hint, children }) => (
  <div className="space-y-1.5">
    <label className="text-[10px] font-black uppercase tracking-widest text-text-muted">{label}</label>
    {children}
    {error && <p className="text-[10px] text-red-500 font-bold">{error}</p>}
    {hint && !error && <p className="text-[10px] text-text-muted">{hint}</p>}
  </div>
);

const ToggleField = ({ label, desc, value, onChange }) => (
  <label className="flex items-center gap-3 cursor-pointer group">
    <div onClick={() => onChange(!value)}
      className={`w-11 h-6 rounded-full relative flex-shrink-0 transition-colors cursor-pointer ${value ? 'bg-accent-gold' : 'bg-brand-dark'}`}>
      <motion.div animate={{ x: value ? 22 : 2 }} transition={{ type: 'spring', stiffness: 500, damping: 30 }}
        className="absolute top-1 w-4 h-4 bg-white rounded-full shadow-sm" />
    </div>
    <div>
      <p className="text-sm font-bold text-accent-navy group-hover:text-accent-gold transition-colors">{label}</p>
      {desc && <p className="text-[10px] text-text-muted">{desc}</p>}
    </div>
  </label>
);

const LoadingSkeleton = () => (
  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
    {[1, 2, 3, 4, 5, 6].map(i => (
      <div key={i} className="bg-white rounded-[28px] border border-brand-dark overflow-hidden animate-pulse">
        <div className="h-40 bg-gradient-to-br from-gray-200 to-gray-300" />
        <div className="p-5 space-y-3">
          <div className="h-3 bg-gray-200 rounded-full w-3/4" />
          <div className="h-3 bg-gray-200 rounded-full w-1/2" />
          <div className="h-10 bg-gray-100 rounded-xl mt-4" />
        </div>
      </div>
    ))}
  </div>
);